import React, { useState, useEffect } from 'react';
import joi from 'joi';
import HOC from '_HOC';
import cts from '_cts';
import { _ } from '_locales';
import { colors } from '_styles';
import apis, { siteKey } from '_apis';
import styled from 'styled-components';
import { WaitScreen } from '_components';
import { useForm } from 'react-hook-form';
import { ReCaptcha } from 'react-recaptcha-v3';
import { createNewRecaptchaToken } from '_helpers';
import { joiResolver } from '@hookform/resolvers/joi';
import { useHistory } from 'react-router-dom';
import { ImageUploader } from '../components';
import store from '../../../configureStore';

const { dispatch } = store.store;

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 580px;
  padding: 45px 35px;
  box-sizing: border-box;
  min-height: 350px;
`;

const Title = styled.h2`
  color: #000;
  font-size: 20px;
  font-weight: 400;
  text-align: left;
  font-family: Roboto, sans-serif;
`;

const Description = styled.p`
  color: #747474;
  font-size: 16px;
  font-weight: 500;
  font-family: Roboto, sans-serif;
  line-height: 1.8em;
`;

const Form = styled.form``;

const Label = styled.label`
  display: none;
`;

const Input = styled.input`
  width: 100%;
  height: 50px;
  display: block;
  padding: 0 10px;
  border-radius: 3px;
  margin-bottom: 15px;
  box-sizing: border-box;
  border: 1px solid #cccccc;
`;

const TextArea = styled.textarea`
  width: 100%;
  resize: none;
  height: 160px;
  display: block;
  border-radius: 3px;
  padding: 15px 10px;
  margin-bottom: 15px;
  box-sizing: border-box;
  border: 1px solid #cccccc;
`;

const SubmitInput = styled.input`
  display: block;
  width: 100%;
  height: 45px;
  text-align: center;
  background-color: ${({ secondaryColor }) => secondaryColor};
  border: none;
  border-radius: 3px;
  padding: 0;
  margin: 0;
  color: #ffffff;
  font-size: 14px !important;
  font-weight: 400 !important;
  cursor: pointer;
`;

const ErrorText = styled.p`
  color: ${colors.RC_RED};
  margin-bottom: 25px;
  font-size: 13px;
`;

const CorrectText = styled.p`
  color: ${colors.RC_GREEN};
  margin-bottom: 25px;
  font-size: 13px;
`;

const phoneRegex = /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;

const formValidator = {
  name: {
    mandatory: joi.string().required(),
    optional: joi.string().allow('').optional(),
    default: joi.string().required(),
  },
  email: {
    mandatory: joi
      .string()
      .required()
      .email({ tlds: { allow: false } })
      .messages({
        'string.empty': _('general.please_provide_your_email_address'),
        'string.email': _('general.invalid_email_address'),
      }),
    optional: joi
      .string()
      .allow('')
      .optional()
      .email({ tlds: { allow: false } })
      .messages({
        'string.empty': _('general.please_provide_your_email_address'),
        'string.email': _('general.invalid_email_address'),
      }),
    default: joi
      .string()
      .required()
      .email({ tlds: { allow: false } })
      .messages({
        'string.empty': _('general.please_provide_your_email_address'),
        'string.email': _('general.invalid_email_address'),
      }),
  },
  phone: {
    mandatory: joi
      .string()
      .pattern(phoneRegex)
      .min(10)
      .required()
      .messages({
        'string.empty': _('general.please_provide_your_number', {
          type: _('general.phone'),
        }),
        'string.pattern.base': _('general.invalid_number', {
          type: _('general.phone'),
        }),
        'string.min': _('general.invalid_number', { type: _('general.phone') }),
        'string.max': _('general.invalid_number', { type: _('general.phone') }),
      }),
    optional: joi
      .string()
      .allow('')
      .optional()
      .pattern(phoneRegex)
      .min(10)
      .messages({
        'string.empty': _('general.please_provide_your_number', {
          type: _('general.phone'),
        }),
        'string.pattern.base': _('general.invalid_number', {
          type: _('general.phone'),
        }),
        'string.min': _('general.invalid_number', { type: _('general.phone') }),
        'string.max': _('general.invalid_number', { type: _('general.phone') }),
      }),
    default: joi
      .string()
      .pattern(phoneRegex)
      .min(10)
      .required()
      .messages({
        'string.empty': _('general.please_provide_your_number', {
          type: _('general.phone'),
        }),
        'string.pattern.base': _('general.invalid_number', {
          type: _('general.phone'),
        }),
        'string.min': _('general.invalid_number', { type: _('general.phone') }),
        'string.max': _('general.invalid_number', { type: _('general.phone') }),
      }),
  },
  description: {
    mandatory: joi.string().required(),
    optional: joi.string().allow('').optional(),
    default: joi.string().required(),
  },
  address: {
    mandatory: joi.string().required(),
    optional: joi.string().allow('').optional(),
    default: joi.string().required(),
  },
};

const FormPage = ({
  plugin, config, help, props
}) => {
  const { fromHelp, damagedCartFormData, helpItems } = help;
  const { location } = props;
  const { projectId } = plugin;
  const { districtId } = config;
  const {
    type, title, description, formDesc, extra_config
  } = location.state || {};
  const { replace } = useHistory();

  const customDamagedPlaceholder = Object.keys(damagedCartFormData).length
    && location.state?.damaged_cart_data
    ? helpItems
      .filter(({ type }) => type === 'DAMAGED_CART')
      .find(
        ({ oidx: index }) => index === Number(location.state?.damaged_cart_data?.oidx)
      )
      .extra_config.find(
        ({ data_page, is_active }) => data_page === 'custom_description_placeholder'
          && is_active === 'true'
      )?.placeholder
    : '';

  const customConfirmationMessage = Object.keys(damagedCartFormData).length
    && location.state?.damaged_cart_data
    ? helpItems
      .filter(({ type }) => type === 'DAMAGED_CART')
      .find(
        ({ oidx: index }) => index === Number(location.state?.damaged_cart_data?.oidx)
      )
      .extra_config.find(
        ({ data_page, is_active }) => data_page === 'custom_confirmation_page' && is_active === 'true'
      )?.text
    : '';

  const damagedCartData = Object.keys(damagedCartFormData).length
    && `${damagedCartFormData?.cartsList?.length
      ? `Damaged carts: ${damagedCartFormData?.cartsList?.join(', ')} \n`
      : ''
    }The carts are: ${damagedCartFormData?.missingOrDamaged} \n${damagedCartFormData?.missingOrDamaged === 'Damaged'
      ? `${damagedCartFormData?.damagedParts?.length
        ? `Damaged parts are: ${damagedCartFormData?.damagedParts}\n`
        : ''
      }`
      : ''
    }${damagedCartFormData?.binNumber
      ? `Bin number: ${damagedCartFormData?.binNumber} \n`
      : ''
    }Other description:`;

  const [token, setToken] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState(false);
  const [picture, setPicture] = useState([]);
  const [loading, setLoading] = useState(false);

  let submit_form_fields = {};
  if (extra_config?.submit_form_fields) {
    submit_form_fields = extra_config?.submit_form_fields;
  }
  if (
    Object.keys(damagedCartFormData).length
    && location.state?.damaged_cart_data
  ) {
    const currentHelpDamagedCartItem = helpItems
      .filter(({ type }) => type === 'DAMAGED_CART')
      .find(
        ({ oidx: index }) => index === location.state.damaged_cart_data.oidx
      ).extra_config;

    submit_form_fields = currentHelpDamagedCartItem[currentHelpDamagedCartItem.length - 1];
  }
  const schema = joi.object({
    name: formValidator.name[submit_form_fields?.name || 'default'],
    email: formValidator.email[submit_form_fields?.email || 'default'],
    address: formValidator.address[submit_form_fields?.address || 'default'],
    description:
      formValidator.description[submit_form_fields?.description || 'default'],
    phone: formValidator.phone[submit_form_fields?.phone || 'default'],
  });

  /* catch the input errors by react hook form */
  const {
    register, handleSubmit, errors, setValue
  } = useForm({
    resolver: joiResolver(schema),
  });

  const verifyCallback = (generatedToken) => {
    setToken(generatedToken);
  };

  useEffect(() => {
    if (formDesc || description) {
      setValue('description', formDesc || description, {
        shouldValidate: true,
      });
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (errors && Object.entries(errors).length) {
          const token = await createNewRecaptchaToken(setToken);

          if (token !== 'done') { throw new Error('There was a problem in generating new token.'); }

          return Promise.resolve(token);
        }

        return undefined;
      } catch (e) {
        return Promise.reject(e);
      }
    })();
  }, [errors]);

  const onSubmit = async (result) => {
    try {
      if (damagedCartFormData?.isImageMandatory && !picture.length) { throw new Error('Image is mandatory'); }
      setLoading(true);

      const items = {
        token,
        can_contact: true,
        from_help: fromHelp,
        problem_type: damagedCartData ? 'DAMAGED_CART' : type,
        project_id: projectId,
        district_id: districtId,
        contact_full_nm: result.name,
        location_text: result.address,
        contact_phone_nbr: result.phone,
        problem_dscr: damagedCartData
          ? `${damagedCartData} ${result.description}`
          : result.description,
        contact_email_addr: result.email,
      };

      if (picture && picture.length) {
        const [pic] = picture;
        items.photo = pic;
      }

      const payLoad = new FormData();

      Object.entries(items).map((item) => payLoad.append(item[0], item[1]));

      const { data, status } = await cts(
        'post',
        apis.problemReporterUrl(),
        payLoad
      );

      if (status !== 200 && data.success) throw setLoading(false);

      setLoading(false);

      const generatedToken = await createNewRecaptchaToken(setToken);
      if (generatedToken !== 'done') { throw new Error('There was a problem in generating new token.'); }

      if (customConfirmationMessage) {
        replace('/help/feedback', { customConfirmationMessage });
      }

      setStatus(_('general.the_form_information_has_sent_successfully'));
      setError(false);
      dispatch({ type: 'SET_HELP_DAMAGED_CART_DATA', data: {} });

      return Promise.resolve(data);
    } catch (e) {
      setLoading(false);
      setStatus(<ErrorText>{_('general.sendData')}</ErrorText>);
      setError(true);

      await createNewRecaptchaToken(setToken);
      console.warn('Error to sending information to server.');
    }
    return null;
  };

  return (
    <Container>
      <ReCaptcha
        sitekey={siteKey}
        action="reportProblem"
        verifyCallback={verifyCallback}
      />
      <Title>{title}</Title>
      <Description>{description}</Description>

      <Form action="submit" onSubmit={handleSubmit(onSubmit)}>
        <Label htmlFor="name">{_('reportProblem.name')}</Label>
        <Input
          id="name"
          type="text"
          name="name"
          ref={register}
          placeholder={_('help.first_name_*')}
        />
        {errors && Object.keys(errors).length && errors.name
          ? Object.keys(errors)
            .filter((item) => item === 'name')
            .map((item) => (
              <ErrorText key={`${item}@${item.name}`}>
                {errors.name.message}
              </ErrorText>
            ))
          : null}

        <Label htmlFor="email">{_('reportProblem.email')}</Label>
        <Input
          id="email"
          type="email"
          name="email"
          ref={register}
          placeholder={_('help.email_*')}
        />
        {errors && Object.keys(errors).length && errors.email
          ? Object.keys(errors)
            .filter((item) => item === 'email')
            .map((item) => (
              <ErrorText key={`${item}@${item.email}`}>
                {errors.email.message}
              </ErrorText>
            ))
          : null}

        <Label htmlFor="address">{_('zone.address')}</Label>
        <Input
          id="address"
          type="text"
          name="address"
          ref={register}
          placeholder={_('help.address_*')}
        />
        {errors && Object.keys(errors).length && errors.address
          ? Object.keys(errors)
            .filter((item) => item === 'address')
            .map((item) => (
              <ErrorText key={`${item}@${item.address}`}>
                {errors.address.message}
              </ErrorText>
            ))
          : null}

        <Label htmlFor="description">{_('reportProblem.description')}</Label>
        <TextArea
          type="text"
          ref={register}
          id="description"
          name="description"
          placeholder={
            customDamagedPlaceholder || _('help.please_describe_the_issue_*')
          }
        />
        {errors && Object.keys(errors).length && errors.description
          ? Object.keys(errors)
            .filter((item) => item === 'description')
            .map((item) => (
              <ErrorText key={`${item}@${item.description}`}>
                {errors.description.message}
              </ErrorText>
            ))
          : null}

        <Label htmlFor="phone">{_('reportProblem.phone')}</Label>
        <Input
          id="phone"
          type="phone"
          name="phone"
          ref={register}
          placeholder={`${_('help.phone_number')}*`}
        />
        {errors && Object.keys(errors).length && errors.phone
          ? Object.keys(errors)
            .filter((item) => item === 'phone')
            .map((item) => (
              <ErrorText key={`${item}@${item.phone}`}>
                {errors.phone.message}
              </ErrorText>
            ))
          : null}

        {/* image uploader box */}
        <ImageUploader pictures={picture} setPictures={setPicture} />
        {damagedCartFormData?.isImageMandatory && !picture.length && (
          <ErrorText>Image is mandatory</ErrorText>
        )}

        <SubmitInput
          type="submit"
          value={_('general.submit')}
          secondaryColor={colors.RC_ACTION}
        />
        {status.length && !error ? <CorrectText>{status}</CorrectText> : null}
        {status.length && error ? <ErrorText>{status}</ErrorText> : null}
      </Form>

      {loading ? <WaitScreen /> : null}
    </Container>
  );
};

export default HOC(FormPage);
